import React from "react";
import { ChamberMemberProPagePermissionsEnum, UserType } from "./graphql/types";
import Sample from "./components/Sample";
import Analytics from "./components/pages/MyChamber/Analytics";
import ManageChamberProfile from "./components/pages/MyChamber/ManageChamberProfile";
import CreateMember from "./components/pages/Members/CreateMember/CreateMember";
import ManageMembers from "./components/pages/Members/ManageMembers/ManageMembers";
import CreateEvent from "./components/pages/Events/CreateEvent/CreateEvent";
import ManageEvents from "./components/pages/Events/ManageEvents/ManageEvents";
import CreateEmail from "./components/pages/Emails/CreateEmail/CreateEmail";
import ManageEmails from "./components/pages/Emails/ManageEmails/ManageEmails";
import ManageInvoices from "./components/pages/Invoicing/ManageInvoices/ManageInvoices";
import Reports from "./components/pages/Reports/Reports";
import Help from "./components/pages/Support/Help";

// DO NOT use any captial letters in any of the redirect URIs cuz it messes with the exact matches
export enum RoutePaths {
  // common pages
  error500 = "/500",
  login = "/login",
  logout = "/logout",
  newPassword = "/newpassword",
  forgotPassword = "/forgotpassword",
  home = "/",

  // chamber admin pages
  chamberAdmin_myChamber_analytics = "/chamber/analytics",
  chamberAdmin_myChamber_manageChamber = "/chamber/manage",

  // members pages
  chamberAdmin_members_create = "/members/create",
  chamberAdmin_members_manage = "/members/manage",

  // events pages
  chamberAdmin_events_create = "/events/create",
  chamberAdmin_events_manage = "/events/manage",

  // website pages
  chamberAdmin_website_builder = "external_url",

  // emails pages
  chamberAdmin_emails_create = "/emails/create",
  chamberAdmin_emails_manage = "/emails/manage",

  // invoicing pages
  chamberAdmin_invoicing_manage = "/invoicing/manage",

  // other pages
  chamberAdmin_reports = "/reports",
  chamberAdmin_perks = "https://dashboard.chamberperksapp.com",
  chamberAdmin_settings = "/settings",
  chamberAdmin_support = "/support",
}

// Define which UserTypes we want to include
type AllowedUserTypes = Extract<
  UserType,
  UserType.ChamberAdmin
  // | UserType.ChamberMemberAdmin
>;

// Helper type to get the correct permission prefix based on UserType
type PermissionPrefix<T extends AllowedUserTypes> =
  T extends UserType.ChamberAdmin
    ? Extract<ChamberMemberProPagePermissionsEnum, `CHAMBER_ADMIN__${string}`>
    : // : T extends UserType.ChamberMemberAdmin
      // ? Extract<
      //     ChamberMemberProPagePermissionsEnum,
      //     `CHAMBER_MEMBER_ADMIN__${string}`
      //   >
      never;

// Update IndividualRoute to be generic
type IndividualRoute<T extends AllowedUserTypes> = {
  pagePermissionNeeded: PermissionPrefix<T>;
  path: RoutePaths;
  element: React.ReactNode;
  icon?: string;
  title?: string;
  description?: string;
  hideInNavBar?: boolean;
  showInBottomNav?: boolean;
  isExternalLink?: boolean;
};

// Update IndividualRouteGroup to be generic
type IndividualRouteGroup<T extends AllowedUserTypes> =
  | {
      nestedRoute: {
        title: string;
        icon: string;
        routes: IndividualRoute<T>[];
      };
      route?: never;
    }
  | {
      route: IndividualRoute<T>;
      nestedRoute?: never;
    };

// Update RouteGroupsType to use the generic versions
type RouteGroupsType = {
  [U in AllowedUserTypes]: IndividualRouteGroup<U>[];
};

export const routeGroups: RouteGroupsType = {
  [UserType.ChamberAdmin]: [
    {
      nestedRoute: {
        title: "My Chamber",
        icon: "home",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_myChamber_analytics,
            element: <Analytics />,
            title: "Analytics",
            description: "",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_myChamber_manageChamber,
            element: <ManageChamberProfile />,
            title: "Manage Chamber Profile",
            description: "",
          },
        ],
      },
    },
    {
      nestedRoute: {
        title: "Members",
        icon: "person",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_members_create,
            element: <CreateMember />,
            title: "Create Member",
            description: "",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_members_manage,
            element: <ManageMembers />,
            title: "Manage Members",
            description: "",
          },
        ],
      },
    },
    {
      nestedRoute: {
        title: "Events",
        icon: "calendar",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_events_create,
            element: <CreateEvent />,
            title: "Create Event",
            description: "",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_events_manage,
            element: <ManageEvents />,
            title: "Manage Events",
            description: "",
          },
        ],
      },
    },
    {
      nestedRoute: {
        title: "Emails",
        icon: "email",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_emails_create,
            element: <CreateEmail />,
            title: "Create Email",
            description: "",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_emails_manage,
            element: <ManageEmails />,
            title: "Manage Previous Emails",
            description: "",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
        path: RoutePaths.chamberAdmin_website_builder,
        element: <Sample />,
        icon: "desktop",
        title: "Website Builder",
        description: "",
        isExternalLink: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
        path: RoutePaths.chamberAdmin_invoicing_manage,
        element: <ManageInvoices />,
        icon: "attach_money",
        title: "Invoices",
        description: "",
      },
    },

    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
        path: RoutePaths.chamberAdmin_reports,
        element: <Reports />,
        icon: "bar_chart",
        title: "Reports",
        description: "Select and generate reports to view chamber analytics",
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
        path: RoutePaths.chamberAdmin_perks,
        element: <Sample />,
        icon: "card_giftcard",
        title: "Chamber Perks App™",
        showInBottomNav: true,
        isExternalLink: true,
      },
    },
    // {
    //   route: {
    //     pagePermissionNeeded:
    //       ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
    //     path: RoutePaths.chamberAdmin_settings,
    //     element: <Sample />,
    //     icon: "settings",
    //     title: "Settings",
    //     showInBottomNav: true,
    //   },
    // },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
        path: RoutePaths.chamberAdmin_support,
        element: <Help />,
        icon: "question_circle",
        title: "Help",
        showInBottomNav: true,
      },
    },
  ],
};

// Derive bottom navigation items from the main routes that have showInBottomNav flag
export const getBottomNavItems = (userType: AllowedUserTypes) => {
  const items: Array<any> = [];

  for (const routeGroup of routeGroups[userType]) {
    if (routeGroup.route && routeGroup.route.showInBottomNav) {
      items.push(routeGroup.route);
    }
  }

  return items;
};

const enumStrings = {
  supportContactEmail: "support@chamberperksapp.com",
  sessionStorageKey: {
    loggedInWithTemporaryPassword: "loggedInWithTemporaryPassword",
  },
  pages: {
    common: {
      errors: {
        invalidUsernameOrPassword: "Invalid username or password",
        networkError: "Please connect to the internet",
        emailDoesNotExist: "Email does not exist please try again",
        tooManyAttempts: "Too many attempts please try again later",
        invalidVerificationCode:
          "Verification code is incorrect please try again",
        userDoesNotHaveAccessToChamberMemberPro:
          "Your chamber does not have access to Chamber Member Pro",
      },
      validation: {
        email: "Email is required",
        password: "Password is required",
        code: "Code is required",
        newPassword: "New password is required",
        confirmPassword: "Confirm password is required",
        passwordsMustMatch: "Passwords must match",
        mustContain8Characters:
          "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character",
      },
      buttons: {
        goBackToLoginText: "Back to sign in",
        homeButtonText: "Go Home",
        contactSupportButtonText: "Contact Support",
      },
    },
    login: {
      forgotPasswordText: "Forgot your password?",
      logoSubtitle: "Sign in to your account",
      buttonText: "Sign In",
    },
    newPassword: {
      logoSubtitle: "Set your password",
      description:
        "You have entered a temporary single-use password. Please set a new permanent password for your account.",
      buttonText: "Set Password",
    },
    forgotPassword: {
      logoSubtitle: "Reset your password",
      description:
        "Enter your account email and we will send you a code to reset your password.",
      buttonText: "Continue",
    },
    resetPassword: {
      pleaseVerifyEmailText: (email: string) =>
        `Please enter the verification code sent to ${email} and your new password.`,
      resetPasswordSuccessText: "Your password has been successfully reset.",
      buttonText: "Reset Password",
      signInButton: "Sign In",
    },
    error500: {
      title: "500",
      subtitle: "An Error Has Occured",
      description: `Sorry, something went wrong on our end. Try refreshing the page by going back or to home. Please contact support if this problem persists.`,
      emailSubject: "Chamber Perks Dashboard 500 Error",
    },
    error404: {
      title: "404",
      subtitle: "Page Not Found",
      description:
        "Sorry, the page you are looking for doesn't exist or has been moved.",
      emailSubject: "Chamber Member Pro 404 Error",
    },
  },
};

export default enumStrings;
